import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ListVinculos from "../../../MlConfiguracoes/Categorias/ListVinculos";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { TCategoria } from "../../../../../../types/api/comercial/categorias";
import DialogSubcategorias from "../../../MlConfiguracoes/Categorias/DialogSubcategorias";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import {
  TProdutoEstoque,
  TProduto,
  TJuntarProdutos,
} from "../../../../../../types/api/comercial/produto-estoque";
import { Controller, useForm } from "react-hook-form";

export default function DialogJuntarProdutos({
  open,
  handleClose,
  produtos,
}: DialogJuntarProdutosProps) {
  const queryClient = useQueryClient();

  const [npenDialogCategorias, setOpenDialogCategorias] = useState(false);
  const [categorias, setCategorias] = useState<TCategoria[]>([]);
  const [categoriaPrincipal, setCategoriaPrincipal] = useState<number | null>(
    null
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      nome: "",
      categorias: [],
      categoriaPrincipal: categoriaPrincipal ?? null,
    },
  });

  const { mutate: juntarProdutos } = useMutation({
    mutationKey: "juntar-produtos",
    mutationFn: (inputs: TJuntarProdutos) =>
      axiosClient.post("comercial/produtos/juntar-produtos", inputs, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao juntar produtos!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["listar-todos-produtos"]);
      handleCloseClear();
      enqueueSnackbar("Junção realizada com sucesso!", {
        variant: "success",
      });
    },
  });

  const onSubmit = (data: any) => {
    const payload = {
      nome: data.nome,
      categoria_principal_id: data.categoriaPrincipal ?? null,
      produtos: produtos.map((produto) => produto.id),
      categorias: categorias.map((categoria) => categoria.id),
    };
    juntarProdutos(payload);
  };

  const handleDeleteItem = (id: number) => {
    setCategorias((prevCategorias) =>
      prevCategorias.filter((cat) => cat.id !== id)
    );
  };

  const handleCloseClear = () => {
    setCategoriaPrincipal(null);
    setCategorias([]);
    reset();
    handleClose();
  };

  useEffect(() => {
    setValue("categoriaPrincipal", categoriaPrincipal);
  }, [categoriaPrincipal, setValue]);

  return (
    <>
      <Dialog open={open} onClose={handleCloseClear} maxWidth="sm" fullWidth>
        <DialogTitle>Juntar produtos</DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid size={{xs: 12, sm: 12}}>
              <Typography variant="subtitle1">
                Items selecionados para a junção
              </Typography>
            </Grid>

            <Grid size={{xs: 12, sm: 12}}>
              <List dense sx={{ padding: 0 }}>
                {produtos.flatMap((produto) =>
                  produto.produto_estoque?.map((estoque: TProdutoEstoque) => (
                    <ListItem key={estoque.id} disablePadding>
                      <ListItemText primary={`• ${estoque.nome}`} />
                    </ListItem>
                  ))
                )}
              </List>
            </Grid>

            <Grid size={{xs: 12, sm: 12}}>
              <Typography variant="subtitle1">
                Para concluir a junção, informe os dados comerciais do novo
                produto
              </Typography>
            </Grid>

            <Grid size={{xs: 12, sm: 12}}>
              <Controller
                name="nome"
                control={control}
                rules={{ required: "Campo obrigatório" }}
                render={({ field }) => (
                  <TextField
                    label="Nome comercial"
                    fullWidth
                    {...field}
                    error={!!errors.nome}
                    helperText={errors.nome ? errors.nome.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid size={{xs: 12, sm: 12}}>
              <ListVinculos
                title="Categorias"
                items={categorias}
                handleOpenDialog={() => setOpenDialogCategorias(true)}
                handleDeleteItem={(id) => handleDeleteItem(id)}
                emptyListMessage="Nenhuma categoria vinculada."
                linkButtonText="Vincular categoria"
                linkButtonIcon={
                  <CategoryOutlinedIcon sx={{ color: "#1b5e20" }} />
                }
                categoriaPrincipal={categoriaPrincipal}
                setCategoriaPrincipal={setCategoriaPrincipal}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleCloseClear}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Juntar
          </Button>
        </DialogActions>
      </Dialog>

      <DialogSubcategorias
        open={npenDialogCategorias}
        handleClose={() => setOpenDialogCategorias(false)}
        value={categorias}
        onChange={(newItems) => setCategorias(newItems ?? [])}
        categoriaId={null}
        breadcrumb={true}
      />
    </>
  );
}

interface DialogJuntarProdutosProps {
  open: boolean;
  handleClose: () => void;
  produtos: TProduto[];
}
